
import { IRatesOfReturn, IRetirementSavingsPersonInputs } from 'app/calcs/retirementSavings'
import { ageAt, IRetirementPerson, isSamePersonForSSA, isValidPerson, personHasRetired, Profile } from 'app/redux/Profile/model'
import { UserActions } from 'app/redux/User/actions'
import { theCensusInfo } from 'app/services/models/PublicCensusInfo'
import { IPokeandPullRequest, IRequestedValues, IWorkbookValues, WorkbookServiceResponse } from 'app/services/models/Workbook'
import { OperationStatus } from 'app/services/OperationStatus'
import { genericFailure } from 'app/services/WebServiceBase'
import { WorkbookService } from 'app/services/WorkbookService'
import * as utils from 'app/utils'
import { makeDebug } from 'app/utils'
import { excelSerialToMoment } from 'app/utils/DateCalcs'
import { INosyParker, nullNosyParker } from 'app/utils/nosyParker'
import { getWebServicesConfig } from 'config'
import { config } from 'dotenv';
import * as _ from 'lodash'
import * as moment from 'moment'

interface ISavingsInputs {
  replaceme: boolean
}

interface ISavingsDAOExtractors {
  member: SavingsDAOPersonExtractors
  spouse: SavingsDAOPersonExtractors
}

interface ISavingsWorkbookRunCalculationsRequest {
  ratesOfReturn: IRatesOfReturn
  member: IRetirementPerson
  memberInputs: IRetirementSavingsPersonInputs
  spouse: IRetirementPerson
  spouseInputs: IRetirementSavingsPersonInputs
}

interface ISavingsBalances {
  when: moment.Moment
  allowedWithdrawal: number
  newSavings: number
  amount: number
  retirementSavings: number
  fourXXX: number
  IRA: number
  personalSavings: number
  ongoingContributions: number
}
type KSavingsBalances = keyof ISavingsBalances
const outputMap: { [key in KSavingsBalances]?: string } = {
  when: 'WHEN',
  allowedWithdrawal: 'WITHDRAWALS',
  newSavings: 'NEW SAVINGS',
  amount: 'TOTAL OF ALL SAVINGS',
  retirementSavings: 'RETIREMENT ACCOUNTS',
  fourXXX: '4xxx BALANCES',
  IRA: 'IRA BALANCE',
  personalSavings: 'PERSONAL SAVINGS',
  ongoingContributions: 'ONGOING CONTRIBUTIONS'
}

const sheetDateFormat = 'L'

// define input sheet and ranges
const inputsSheetName = 'Inputs'
const calcStartDateRange = 'c2'
const inputsRange = 'c5:d23'
const inputsSpouseRange = 'c15:d23'
const reflectInputsRange = 'b5:d23'

// define output sheet and ranges
const outputsSheetName = 'Outputs'
const memberFinalBalanceCell = 'e2'
const memberOutputsRange = 'b3:l800'
const spouseFinalBalanceCell = 'q2'
const spouseOutputsRange = 'n3:v800'

// define assumption sheet and ranges
const assumptionsNotesSheetName = 'Assumptions - Notes'
const memberLevelWithdrawalAmountCell = 'c25'
const spouseLevelWithdrawalAmountCell = 'g25'

const log = makeDebug(false)

function momentToMonths(when: moment.Moment): number {
  return when.year() * 12 + when.month()
}

class SavingsDAOPersonExtractors {
  balances: any[]
  columnNames: any[]
  firstBalanceAsMonth: number

  constructor(table, public finalBalance, public levelWithdrawalAmount) {
    log('levelWithdrawalAmount: ' + levelWithdrawalAmount + ', final balance: ' + finalBalance)
    this.columnNames = _.first(table)
    this.balances = _.drop(table, 1)
    _.forEach(this.balances, (row: any[]) => {
      row[0] = excelSerialToMoment(row[0]).endOf('month')
    })
    this.firstBalanceAsMonth = momentToMonths(this.balances[0][0])
  }

  balancesFromRow(row: any): ISavingsBalances {
    if (!row) { return undefined }
    return _.mapValues(outputMap, (v, k) => {
      const column = this.columnNameToIndex(v)
      return row[column] || 0
    }) as ISavingsBalances
  }

  columnNameToIndex(columnName: string): number {
    return _.findIndex(this.columnNames, (q) => q === columnName)
  }

  findRow(w: moment.Moment): any {
    const m = momentToMonths(w)
    const em = m - this.firstBalanceAsMonth
    const b = this.balances[em]
    if (!b) { return undefined }
    if (m !== momentToMonths(b[0])) {
      // ack, landed in the wrong place!
      // tslint:disable-next-line:no-debugger
      //debugger
    }
    return b
    //return _.find(balances, (q) => w.isSame(q[0], 'month'))
  }

  findRowIndex(w: moment.Moment) {
    const m = momentToMonths(w)
    const em = m - this.firstBalanceAsMonth
    const b = this.balances[em]
    if (!b) { return undefined }
    if (m !== momentToMonths(b[0])) {
      // ack, landed in the wrong place!
      // tslint:disable-next-line:no-debugger
      //debugger
    }
    return em
  }

  getAllBalances(): ISavingsBalances[] {
    return _.map(this.balances, (row) => this.balancesFromRow(row))
  }

  getBalances(w: moment.Moment): ISavingsBalances {
    const row = this.findRow(w)
    return this.balancesFromRow(row)
  }

}

/**
 * Makes a set of functions that can extract information from the workbook response.
 */
function makeExtractors() {
  return (wr: WorkbookServiceResponse): ISavingsDAOExtractors => {
    if (!wr.Results) {
      // tslint:disable-next-line:no-console
      console.error('makeExtractors- DAO call failed. ' + wr)
      return
    }
    const outputSheet = wr.Results[outputsSheetName]
    const assumptionsNotesSheet = wr.Results[assumptionsNotesSheetName]

    return {
      member: new SavingsDAOPersonExtractors(outputSheet[memberOutputsRange], outputSheet[memberFinalBalanceCell], assumptionsNotesSheet[memberLevelWithdrawalAmountCell]),
      spouse: new SavingsDAOPersonExtractors(outputSheet[spouseOutputsRange], outputSheet[spouseFinalBalanceCell], assumptionsNotesSheet[spouseLevelWithdrawalAmountCell])
    }
  }
}

// tslint:disable-next-line:max-classes-per-file
class SavingsWorkbookDAO {
  private service: WorkbookService = new WorkbookService(getWebServicesConfig().workbookEndpoint, 'savingsWorkbook')

  constructor(protected nosyParker: INosyParker = nullNosyParker) { }

  /**
   * Run the Savings calculations workbook.
   *
   * @param request The request parameters
   *
   * @returns a WorkbookServiceResponse.
   */
  runCalculations(request: ISavingsWorkbookRunCalculationsRequest): Promise<WorkbookServiceResponse> {

    this.nosyParker.operationIsStarting()
    const startTime = moment.utc()

    return this.issueRequest(request)
      .then((wr: WorkbookServiceResponse) => {
        const elapsed = moment.utc().diff(startTime)

        log('It took ' + elapsed + 'ms for SavingsWorkbookDAO calculations')

        if (wr.IsError || !wr.Results) {
          // tslint:disable-next-line:no-debugger
          //debugger
          this.nosyParker.operationHasFailed(wr)
        } else {
          this.nosyParker.operationHasCompleted(wr)
        }

        return wr
      })
      .catch((reason) => {
        this.nosyParker.operationHasFailed(reason)
        return Promise.reject(reason)
      })
  }

  issueRequest(request: ISavingsWorkbookRunCalculationsRequest): Promise<WorkbookServiceResponse> {

    const rateOfReturn = request.ratesOfReturn.default / 100.0

    // prepare the inputs expected by the workbook

    // for 697, we don't use iraBalance. Instead supply healthReimbursementAccount
    const bal1 = request.memberInputs.fixedBalances.iraBalance
      || request.memberInputs.fixedBalances.healthReimbursementAccount
      || 0

    const inputs: IWorkbookValues = {
      [inputsSheetName]: {
        [calcStartDateRange]: theCensusInfo.censusDate || moment.utc(),
        [inputsRange]: [

          // member data
          [request.member.dob.format(sheetDateFormat)],
          [request.memberInputs.fixedBalances.retirementSavings, rateOfReturn],
          [request.memberInputs.fixedBalances.fourxxBalance || 0, rateOfReturn],
          [bal1, rateOfReturn],
          [request.memberInputs.fixedBalances.personalSavings || 0, rateOfReturn],
          [request.memberInputs.annualContributions.amount, rateOfReturn],
          [request.memberInputs.annualContributions.stopWhen.as('years')],
          [request.memberInputs.withdrawals.startWhen.as('years')],
          [request.memberInputs.withdrawals.monthlyAmount || 0],
          [request.member.selectedAgeExpectancy],

          // placeholder for spouse data
          [''],
          [0],
          [0],
          [0],
          [0],
          [0],
          [0],
          [0],
          [0]
        ]
      }
    }

    // if a spouse was supplied, add their information to the workbook inputs
    if (isValidPerson(request.spouse) && request.spouseInputs.annualContributions.stopWhen) {
      inputs[inputsSheetName][inputsSpouseRange] = [
        [request.spouse.dob.format(sheetDateFormat)],
        [request.spouseInputs.fixedBalances.fourxxBalance || 0, rateOfReturn],
        [request.spouseInputs.fixedBalances.iraBalance || 0, rateOfReturn],
        [request.spouseInputs.fixedBalances.personalSavings || 0, rateOfReturn],
        [request.spouseInputs.annualContributions.amount, rateOfReturn],
        [request.spouseInputs.annualContributions.stopWhen.as('years')],
        [request.spouseInputs.withdrawals.startWhen.as('years')],
        [request.spouseInputs.withdrawals.monthlyAmount || 0],
        [request.spouse.selectedAgeExpectancy],
      ]
    }

    // setup the cells that we want back
    const outputs: IRequestedValues = {
      [assumptionsNotesSheetName]: [memberLevelWithdrawalAmountCell, spouseLevelWithdrawalAmountCell],
      [inputsSheetName]: [calcStartDateRange, reflectInputsRange],
      [outputsSheetName]: [memberFinalBalanceCell, memberOutputsRange, spouseFinalBalanceCell, spouseOutputsRange]
    }

    // actually run the workbook
    const req: IPokeandPullRequest = { inputs, outputs }
    return this.service.pokeAndPull(UserActions.getSharedVar('accessToken'), req)
  }

}

const theSavingsWorkbookDAO = new SavingsWorkbookDAO()
const parseResponse = makeExtractors()

function calculateSavings(request: ISavingsWorkbookRunCalculationsRequest) {
  return theSavingsWorkbookDAO
    .runCalculations(request)
    .then(parseResponse)
}

type TSavingsCalculator = (request: ISavingsWorkbookRunCalculationsRequest) => any
const runCalculator = _.memoize(calculateSavings, JSON.stringify)

export {
  ISavingsWorkbookRunCalculationsRequest,
  ISavingsBalances,
  ISavingsDAOExtractors,
  SavingsWorkbookDAO,
  SavingsDAOPersonExtractors,
  TSavingsCalculator,

  runCalculator
}



// WEBPACK FOOTER //
// ./src/app/workbookDAOs/SavingsWorkbookDAO/index.ts